import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { openOrCloseGlobalDialog } from '../../action/dialog/global-dialog';
import { cancelScheduledText } from '../../contacts/actions/cancel-scheduled-text';
import { updateScheduledMessage } from '../../contacts/actions/update-scheduled-message';
import { fullApplicationSelector } from '../../reducers/application-selectors';

import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { MessageItem } from '../../messages/components/message-item';
import { EmailMessage } from '../../messages/legacy-components/email-message';
import { TextMessage } from '../../messages/legacy-components/text-message';
import { UiNotification } from '../../ui/ui-notification';

import { getEmailSubject } from '../../automations/utils/applicant-automation-display-utils';
import { formatDateTime, formatRelativeDate } from '../../util/formatters';
import { LanguageCode } from '../../util/languages';
import { MESSAGE_TYPE } from '../../util/message-types';
import { SORT_ORDER, sortByDate } from '../../util/sort-functions';
import { collectMessageToLanguage } from '../../widgets/message-composer/wrappers/multiple-message-composer-dialog';

const mapStateToProps = (state: any, { jobApplicantId }: InputProps) => ({
  jobApplicant: fullApplicationSelector(state, jobApplicantId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { jobApplicantId }: InputProps,
) => ({
  actions: {
    updateScheduledMessage: (
      contactId: number,
      scheduledMessageId: number,
      update: Partial<ScheduledMessage>,
    ) => dispatch(updateScheduledMessage(contactId, scheduledMessageId, update)),
    cancelScheduledMessage: (contactId: number, scheduledMessageId: number) =>
      dispatch(cancelScheduledText(contactId, scheduledMessageId)),
    openApplicantDialog: () =>
      dispatch(
        openOrCloseGlobalDialog('applicantDialog', true, {
          jobApplicantId,
        }),
      ),
  },
});

type InputProps = {
  jobApplicantId: number;
  scheduledTextMessages: Array<ScheduledMessage>;
  onClose: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const ScheduledMessageNotification = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ onClose, scheduledTextMessages, jobApplicant, actions }: Props) => {
  // Practically this shouldn't happen, but just in case there's some misconfiguration or misuse, bail out if we don't have scheduled messages
  if (scheduledTextMessages.length === 0) {
    return null;
  }
  // Get the first message that will be sent out and show that in the notification (some status change messages have multiple messages that are scheduled)
  const [scheduledMessage] = scheduledTextMessages.sort(
    sortByDate((sm) => sm.sendDateTime, SORT_ORDER.ASC),
  );

  const handleEditScheduledMessage = async () => {
    const languageToMessage: Record<LanguageCode, string> = {
      en: scheduledMessage.message,
    };
    const languagesToSupport = ['en'];

    const returnVal = await collectMessageToLanguage(
      'Edit Scheduled Message',
      <>
        Edit this message. It will be sent to{' '}
        <span role="presentation" className="typo-link" onClick={actions.openApplicantDialog}>
          {jobApplicant.firstName.trim()} {jobApplicant.lastName.trim()}
        </span>{' '}
        {formatRelativeDate(scheduledMessage.sendDateTime)}, on{' '}
        {formatDateTime(scheduledMessage.sendDateTime)}.
      </>,
      {
        enableText: scheduledMessage.messageType === MESSAGE_TYPE.TEXT,
        enableEmail: scheduledMessage.messageType === MESSAGE_TYPE.EMAIL,
        languageToSubject: {
          en: scheduledMessage.subject ?? getEmailSubject(jobApplicant.jobTitle),
        },
        languageToMessage,
        languagesToSupport,
        buttonClass: 'u-float-right',
        buttonText: 'Save Message',
      },
    );

    if (returnVal) {
      actions.updateScheduledMessage(
        scheduledMessage.contactId,
        scheduledMessage.scheduledMessageId,
        {
          message: returnVal.languageToMessage.en,
          subject: returnVal.languageToSubject.en,
        },
      );
    }
  };

  const handleDeleteScheduledMessage = async () => {
    scheduledTextMessages.forEach((sm) => {
      actions.cancelScheduledMessage(sm.contactId, sm.scheduledMessageId);
    });
    onClose();
  };

  const MessageComponent = (sm: ScheduledMessage) => (
    <>
      <FeatureFlagged
        variant={FeatureGatedVariant.HIDE_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        {scheduledMessage.messageType === MESSAGE_TYPE.EMAIL ? (
          <EmailMessage
            messageBody={sm.message}
            subject={sm.subject || getEmailSubject(jobApplicant.jobTitle)}
            senderName="Team Engine (automatically)"
            isAutomated
            onEditMessage={handleEditScheduledMessage}
            maxCharacters={200}
          />
        ) : (
          <TextMessage
            messageBody={sm.message}
            isAutomated
            onEditMessage={handleEditScheduledMessage}
            maxCharacters={200}
          />
        )}
      </FeatureFlagged>
      <FeatureFlagged
        variant={FeatureGatedVariant.SHOW_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        <MessageItem
          messageType={scheduledMessage.messageType}
          messageBody={sm.message}
          subject={sm.subject || getEmailSubject(jobApplicant.jobTitle)}
          senderName="Team Engine (automatically)"
          onEditMessage={handleEditScheduledMessage}
          maxCharacters={200}
          isScheduledToSend
          isPreview
        />
      </FeatureFlagged>
    </>
  );

  return (
    <UiNotification
      title={`Scheduled this message to send ${formatRelativeDate(scheduledMessage.sendDateTime)}`}
      onClose={onClose}
      notificationBody={MessageComponent(scheduledMessage)}
      actions={
        <div className="u-margin-top u-display-flex u-justifyContent-spaceBetween">
          <div role="presentation" className="typo-link" onClick={handleDeleteScheduledMessage}>
            Don&apos;t Send
          </div>
          <div role="presentation" className="typo-link" onClick={onClose}>
            Dismiss
          </div>
        </div>
      }
    />
  );
});
